import ThemePlaceholder from "components/Common/ThemePlaceholder"
import { Badge, Card, CardBody } from "reactstrap"
import { formatReadableDate, formatSinceDate } from "utils/formatDate"
import {
  formatCustomerTypeLabel,
  getCustomerDepartmentText,
  getCustomerOrganizationText,
} from "helpers/customerOnboarding"

const coverStyle = {
  background: "linear-gradient(135deg, #f8fbff 0%, #eef7ff 48%, #e4f0ff 100%)",
  borderBottom: "1px solid rgba(85, 110, 230, 0.12)",
}

const coverTitleStyle = {
  color: "#2f3b63",
}

const coverSubtitleStyle = {
  color: "#6c7aa5",
}

const getText = value => {
  if (value === null || value === undefined || value === "") return "-"
  if (typeof value === "object" && value?.name) return value.name
  return String(value)
}

const detailRows = customer => {
  return [
    { label: "Customer ID", value: customer?.cuid || "-" },
    { label: "Mobile", value: customer?.mobile || "-" },
    { label: "Email", value: customer?.email || "-" },
    { label: "Designation", value: getText(customer?.designation) },
    { label: "Branch", value: getCustomerDepartmentText(customer) || "-" },
    { label: "Organization", value: getCustomerOrganizationText(customer) || "-" },
    { label: "Address", value: customer?.full_address || "-" },
  ]
}

export default function ProfileCard({
  data,
  loading = false,
  onCustomer,
}) {
  if (!loading && !data) return null

  const rows = detailRows(data)
  const quotationCount = data?.quotations?.length ?? 0
  const moneyReceiptCount = data?.money_receipts?.length ?? 0

  return (
    <Card className="border-0 shadow-sm overflow-hidden mb-4">
      <div className="px-4 pt-4 pb-5" style={coverStyle}>
        {loading ? (
          <ThemePlaceholder
            style={{
              width: 160,
              height: 28,
              borderRadius: 8,
              backgroundColor: "rgba(85,110,230,.12)",
            }}
          />
        ) : (
          <div className="d-flex justify-content-between align-items-start gap-3">
            <div>
              <h5 className="mb-1" style={coverTitleStyle}>Customer Profile</h5>
              <p className="mb-0" style={coverSubtitleStyle}>
                Core identity, relationship, and activity overview
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              {onCustomer ? <>{onCustomer}</> : null}
              {data?.customer_type ? (
                <Badge className="badge-soft-primary border-0">
                  {formatCustomerTypeLabel(data.customer_type)}
                </Badge>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <CardBody className="p-4" style={{ marginTop: -42 }}>
        <div className="d-flex align-items-end gap-3 mb-4">
          <div
            className="rounded-4 overflow-hidden border border-3 border-white bg-white shadow-sm flex-shrink-0"
            style={{ width: 104, height: 104 }}
          >
            {loading ? (
              <ThemePlaceholder style={{ width: "100%", height: "100%", borderRadius: 0 }} />
            ) : (
              <div
                className="w-100 h-100 d-flex align-items-center justify-content-center text-primary fw-bold"
                style={{ fontSize: 36, background: "#eef2ff" }}
              >
                {(data?.name || "C").charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          <div className="min-width-0 flex-grow-1">
            {loading ? (
              <>
                <ThemePlaceholder style={{ width: 220, height: 26, borderRadius: 8 }} />
                <ThemePlaceholder className="mt-2" style={{ width: 180, height: 18, borderRadius: 8 }} />
                <ThemePlaceholder className="mt-2" style={{ width: 200, height: 18, borderRadius: 8 }} />
              </>
            ) : (
              <>
                <h4 className="mb-1 text-truncate">{data?.name || "Customer"}</h4>
                <p className="text-muted mb-1 text-truncate">
                  {getText(data?.designation) !== "-" ? getText(data?.designation) : "No designation assigned"}
                </p>
                <p className="text-muted mb-0 text-truncate">
                  {getCustomerOrganizationText(data) || "No organization assigned"}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-4">
          {loading ? (
            <>
              <ThemePlaceholder style={{ width: 110, height: 30, borderRadius: 999 }} />
              <ThemePlaceholder style={{ width: 120, height: 30, borderRadius: 999 }} />
            </>
          ) : (
            <>
              <Badge color="light" className="text-primary border px-3 py-2">
                {quotationCount} quotations
              </Badge>
              <Badge color="light" className="text-success border px-3 py-2">
                {moneyReceiptCount} money receipts
              </Badge>
            </>
          )}
        </div>

        <div className="specs-list">
          {rows.map(item => (
            <div
              key={item.label}
              className="d-flex justify-content-between py-2 border-bottom gap-3"
            >
              <span className="text-muted">{item.label}:</span>
              {loading ? (
                <ThemePlaceholder style={{ width: 140, height: 18, borderRadius: 4 }} />
              ) : (
                <span className="fw-medium text-end">{item.value}</span>
              )}
            </div>
          ))}
        </div>

        {!loading && data?.created_at ? (
          <div className="pt-3 mt-3 border-top">
            <div className="small text-muted">Member Since</div>
            <div>{formatReadableDate(data.created_at)}</div>
            <div className="small text-muted fst-italic">
              {formatSinceDate(data.created_at)}
            </div>
          </div>
        ) : null}
      </CardBody>
    </Card>
  )
}
